import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MenuIcon, XIcon, ChevronDownIcon, ZapIcon, ShieldCheckIcon, TargetIcon, MapPinIcon, StarIcon, GlobeIcon, TrendingUpIcon, DollarSignIcon, PhoneIcon, MailIcon } from 'lucide-react';
import { ProofCarousel } from './ProofCarousel';
import emailjs from '@emailjs/browser';
const EMAILJS_SERVICE_ID = 'service_5st6tfj';
const EMAILJS_TEMPLATE_ADMIN = 'template_kwi4ynr';
const EMAILJS_TEMPLATE_AUTOREPLY = 'template_2deikke';
const EMAILJS_PUBLIC_KEY = 'vWvHy5XmFe6E1h_h6';
const LOGO_URL = 'https://cdn.magicpatterns.com/uploads/6TdzGPbST4y3Gk6CzyrcQT/IMG_6635.jpg';
const dropdownVariants = {
  hidden: {
    opacity: 0,
    y: -10
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.2,
      ease: 'easeOut'
    }
  },
  exit: {
    opacity: 0,
    y: -10,
    transition: {
      duration: 0.15,
      ease: 'easeIn'
    }
  }
};
const mobileMenuVariants = {
  hidden: {
    opacity: 0,
    x: '100%'
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.3,
      ease: 'easeOut'
    }
  },
  exit: {
    opacity: 0,
    x: '100%',
    transition: {
      duration: 0.3,
      ease: 'easeIn'
    }
  }
};
const accordionVariants = {
  hidden: {
    opacity: 0,
    height: 0
  },
  visible: {
    opacity: 1,
    height: 'auto',
    transition: {
      duration: 0.3
    }
  },
  exit: {
    opacity: 0,
    height: 0,
    transition: {
      duration: 0.2
    }
  }
};
export function NavigationHeaderEN() {
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [mobileExpandedSection, setMobileExpandedSection] = useState<string | null>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (headerRef.current && !headerRef.current.contains(event.target as Node)) {
        setActiveDropdown(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);
  const toggleDropdown = (section: string) => {
    setActiveDropdown(activeDropdown === section ? null : section);
  };
  const toggleMobileSection = (section: string) => {
    setMobileExpandedSection(mobileExpandedSection === section ? null : section);
  };
  const closeAll = () => {
    setActiveDropdown(null);
    setIsMobileMenuOpen(false);
  };
  const navItems = [{
    id: 'agencia',
    label: 'THE AGENCY'
  }, {
    id: 'sistema',
    label: 'ACQUISITION SYSTEM'
  }, {
    id: 'casos',
    label: 'SUCCESS STORIES'
  }, {
    id: 'contacto',
    label: 'CONTACT / SUPPORT'
  }];
  return <div ref={headerRef} className="fixed top-0 left-0 right-0 z-50" data-id="element-146">
      <div className="bg-[#000000] border-b border-[#D4A843]/20 relative z-50" data-id="element-147">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between" data-id="element-148">
          <a href="#" onClick={closeAll} className="flex-shrink-0" data-id="element-149">
            <img src={LOGO_URL} alt="Doctor Ecommerce Logo" className="h-20 md:h-24" data-id="element-150" />
          </a>

          <nav className="hidden lg:flex items-center gap-8" data-id="element-151">
            {navItems.map(item => <button key={item.id} onClick={() => toggleDropdown(item.id)} className={`text-sm font-semibold tracking-wide transition-colors flex items-center gap-1 ${activeDropdown === item.id ? 'gold-text' : 'text-gray-300 hover:text-white'}`} data-id="element-152">
                {item.label}
                <ChevronDownIcon className={`w-4 h-4 transition-transform duration-200 ${activeDropdown === item.id ? 'rotate-180' : ''}`} data-id="element-153" />
              </button>)}
          </nav>

          <div className="hidden lg:block" data-id="element-154">
            <a href="#formulario" onClick={closeAll} className="inline-block gold-gradient text-black font-bold px-6 py-2.5 rounded-lg text-sm gold-glow gold-glow-hover transition-all duration-300" data-id="element-155">
              BOOK FREE DIAGNOSIS
            </a>
          </div>

          <button className="lg:hidden text-white p-2" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu" data-id="element-156">
            {isMobileMenuOpen ? <XIcon className="w-6 h-6" data-id="element-157" /> : <MenuIcon className="w-6 h-6" data-id="element-158" />}
          </button>
        </div>
      </div>

      <div className="hidden lg:block relative" data-id="element-159">
        <AnimatePresence data-id="element-160">
          {activeDropdown && <motion.div initial="hidden" animate="visible" exit="exit" variants={dropdownVariants} className="absolute top-0 left-0 right-0 bg-[#0A0A0A] border-b border-[#D4A843]/30 shadow-2xl shadow-black/50 overflow-hidden" data-id="element-161">
              <div className="max-w-6xl mx-auto px-8 py-12" data-id="element-162">
                {activeDropdown === 'agencia' && <AgenciaContent data-id="element-163" />}
                {activeDropdown === 'sistema' && <SistemaContent data-id="element-164" />}
                {activeDropdown === 'casos' && <CasosContent closeAll={closeAll} data-id="element-165" />}
                {activeDropdown === 'contacto' && <ContactoContent data-id="element-166" />}
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>

      <AnimatePresence data-id="element-167">
        {isMobileMenuOpen && <motion.div initial="hidden" animate="visible" exit="exit" variants={mobileMenuVariants} className="fixed inset-0 z-40 bg-[#0A0A0A] overflow-y-auto pt-32 pb-24 px-4 lg:hidden" data-id="element-168">
            <div className="flex flex-col gap-2" data-id="element-169">
              {navItems.map(item => <div key={item.id} className="border-b border-gray-800" data-id="element-170">
                  <button onClick={() => toggleMobileSection(item.id)} className="w-full flex items-center justify-between py-4 text-left font-bold text-white" data-id="element-171">
                    <span className={mobileExpandedSection === item.id ? 'gold-text' : ''} data-id="element-172">
                      {item.label}
                    </span>
                    <ChevronDownIcon className={`w-5 h-5 transition-transform duration-200 ${mobileExpandedSection === item.id ? 'rotate-180 text-[#D4A843]' : 'text-gray-500'}`} data-id="element-173" />
                  </button>
                  <AnimatePresence data-id="element-174">
                    {mobileExpandedSection === item.id && <motion.div initial="hidden" animate="visible" exit="exit" variants={accordionVariants} className="overflow-hidden" data-id="element-175">
                        <div className="pb-6 pt-2" data-id="element-176">
                          {item.id === 'agencia' && <AgenciaContent isMobile data-id="element-177" />}
                          {item.id === 'sistema' && <SistemaContent isMobile data-id="element-178" />}
                          {item.id === 'casos' && <CasosContent isMobile closeAll={closeAll} data-id="element-179" />}
                          {item.id === 'contacto' && <ContactoContent isMobile data-id="element-180" />}
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>)}
            </div>

            <div className="mt-8" data-id="element-181">
              <a href="#formulario" onClick={closeAll} className="block w-full text-center gold-gradient text-black font-bold px-6 py-4 rounded-lg text-base gold-glow transition-all duration-300" data-id="element-182">
                BOOK FREE DIAGNOSIS
              </a>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
function AgenciaContent({
  isMobile = false
}: {
  isMobile?: boolean;
}) {
  return <div className="max-w-3xl mx-auto text-center" data-id="element-183">
      <h2 className={`gold-text font-bold mb-6 ${isMobile ? 'text-2xl' : 'text-4xl'}`} data-id="element-184">
        We are not creatives. We are specialists in your revenue.
      </h2>
      <div className="border-l-4 border-[#D4A843] pl-6 text-left" data-id="element-185">
        <p className="text-gray-300 mb-4 text-base md:text-lg leading-relaxed" data-id="element-186">
          While other agencies focus on making your Instagram look 'pretty' or
          giving you reports full of technical jargon you don't understand, at
          Doctor Ecommerce we have one single obsession:{' '}
          <span className="text-white font-semibold" data-id="element-187">
            Making your phone ring non-stop.
          </span>
        </p>
        <p className="text-gray-300 text-base md:text-lg leading-relaxed" data-id="element-188">
          We were born in the heart of Tampa to rescue local business owners who
          have been deceived by 'gurus' and agencies that only gamble with your
          money. We understand your sacrifice, and we apply precision
          engineering so that every dollar you invest returns multiplied in real
          clients. We are not your agency, we are the growth department your
          business needed to dominate the US market.
        </p>
      </div>
    </div>;
}
function SistemaContent({
  isMobile = false
}: {
  isMobile?: boolean;
}) {
  const services = [{
    icon: ZapIcon,
    title: 'Direct Response Advertising',
    desc: 'Meta Ads & Google Ads configured to generate calls and appointments, not likes.'
  }, {
    icon: ShieldCheckIcon,
    title: 'Account Shielding',
    desc: 'Anti-ban structure and high-trust agency profiles.'
  }, {
    icon: TargetIcon,
    title: 'Authority Conversion',
    desc: 'Creation of Landing Pages that eliminate your competition in 5 seconds.'
  }, {
    icon: MapPinIcon,
    title: 'Local Dominance (SEO & Maps)',
    desc: 'Verification and optimization so you appear first when they search for your service.'
  }, {
    icon: StarIcon,
    title: 'Reputation Management',
    desc: 'Automated review system so social proof sells for you.'
  }, {
    icon: GlobeIcon,
    title: 'Digital PR',
    desc: 'Positioning in media outlets to elevate your authority to celebrity level in your niche.'
  }];
  const niches = [{
    category: 'Construction & Home',
    items: ['Roofing', 'Landscaping', 'Moving', 'Handyman', 'Plumbing', 'HVAC', 'Home Remodeling', 'Cleaning Services', 'Painting', 'Solar Panels']
  }, {
    category: 'Health & Beauty',
    items: ['Beauty Salons', 'Spas', 'Estheticians', 'Barber Shops', 'Plastic Surgeons', 'Dentists', 'Medical Clinics', 'Pet Grooming']
  }, {
    category: 'Professional Services',
    items: ['Lawyers (Immigration/Accidents)', 'Accountants', 'Real Estate', 'Notaries', 'Insurance']
  }, {
    category: 'Gastronomy',
    items: ['Restaurants', 'Food Trucks', 'Bakeries', 'Catering']
  }, {
    category: 'Automotive',
    items: ['Auto Repair', 'Detailing', 'Body Shops', 'Car Sales']
  }];
  return <div data-id="element-189">
      <h2 className={`gold-text font-bold mb-8 text-center ${isMobile ? 'text-2xl' : 'text-3xl'}`} data-id="element-190">
        Elite Infrastructure for Businesses Looking to Scale.
      </h2>

      <div className={`grid gap-6 mb-10 ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`} data-id="element-191">
        {services.map((service, idx) => <div key={idx} className="flex items-start gap-4" data-id="element-192">
            <div className="mt-1 flex-shrink-0" data-id="element-193">
              <service.icon className="w-6 h-6 text-[#D4A843]" data-id="element-194" />
            </div>
            <div data-id="element-195">
              <h4 className="text-white font-bold mb-1" data-id="element-196">{service.title}</h4>
              <p className="text-gray-400 text-sm" data-id="element-197">{service.desc}</p>
            </div>
          </div>)}
      </div>

      <div className="border-t border-gray-800 pt-8" data-id="element-198">
        <h3 className="text-xl text-white font-bold mb-6 text-center" data-id="element-199">
          Niches and Services We Dominate (USA):
        </h3>
        <div className="space-y-6" data-id="element-200">
          {niches.map((niche, idx) => <div key={idx} data-id="element-201">
              <h4 className="gold-text text-sm font-bold mb-3 uppercase tracking-wider" data-id="element-202">
                {niche.category}
              </h4>
              <div className="flex flex-wrap gap-2" data-id="element-203">
                {niche.items.map((item, i) => <span key={i} className="bg-[#111] border border-gray-800 rounded-full px-3 py-1 text-xs text-gray-300" data-id="element-204">
                    {item}
                  </span>)}
              </div>
            </div>)}
        </div>
      </div>
    </div>;
}
function CasosContent({
  isMobile = false,
  closeAll
}: {
  isMobile?: boolean;
  closeAll: () => void;
}) {
  return <div data-id="element-205">
      <h2 className={`gold-text font-bold mb-8 text-center ${isMobile ? 'text-2xl' : 'text-3xl'}`} data-id="element-206">
        Real Stories of Businesses That Stopped Surviving and Started
        Dominating.
      </h2>

      <div className="mb-10 -mx-8" data-id="element-207">
        <ProofCarousel compact data-id="element-208" />
      </div>

      <div className={`grid gap-6 mb-8 ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`} data-id="element-209">
        <div className="bg-[#1A1A1A] border border-[#D4A843]/30 rounded-xl p-6 relative overflow-hidden group hover:border-[#D4A843]/60 transition-colors" data-id="element-210">
          <div className="absolute top-4 right-4 opacity-10 group-hover:opacity-20 transition-opacity" data-id="element-211">
            <TrendingUpIcon className="w-24 h-24 text-[#D4A843]" data-id="element-212" />
          </div>
          <div className="relative z-10" data-id="element-213">
            <span className="gold-text text-xs font-bold uppercase tracking-wider mb-2 block" data-id="element-214">
              Roofing — Texas
            </span>
            <h3 className="text-xl text-white font-bold mb-4" data-id="element-215">
              The Rebirth of a Roofing Company in Texas
            </h3>
            <p className="text-gray-300 italic text-sm leading-relaxed" data-id="element-216">
              "They went from 2 calls a week to having their schedule full a
              month in advance. We implemented the A.C.E. System and achieved a
              12x ROAS in the first 15 days."
            </p>
          </div>
        </div>

        <div className="bg-[#1A1A1A] border border-[#D4A843]/30 rounded-xl p-6 relative overflow-hidden group hover:border-[#D4A843]/60 transition-colors" data-id="element-217">
          <div className="absolute top-4 right-4 opacity-10 group-hover:opacity-20 transition-opacity" data-id="element-218">
            <DollarSignIcon className="w-24 h-24 text-[#D4A843]" data-id="element-219" />
          </div>
          <div className="relative z-10" data-id="element-220">
            <span className="gold-text text-xs font-bold uppercase tracking-wider mb-2 block" data-id="element-221">
              Spa — Miami
            </span>
            <h3 className="text-xl text-white font-bold mb-4" data-id="element-222">
              Total Dominance in a Miami Spa
            </h3>
            <p className="text-gray-300 italic text-sm leading-relaxed" data-id="element-223">
              "The owner was about to close due to lack of clients. We
              redesigned her irresistible offer and today she bills over $40k
              extra per month."
            </p>
          </div>
        </div>
      </div>

      <div className="text-center mb-8" data-id="element-224">
        <h4 className="text-white font-bold mb-2" data-id="element-225">Live Reviews</h4>
        <p className="text-gray-500 italic text-sm max-w-2xl mx-auto" data-id="element-226">
          Real WhatsApp screenshots from grateful business owners and photos of
          their teams in action.
        </p>
      </div>

      <div className="text-center" data-id="element-227">
        <a href="#casos" onClick={closeAll} className="inline-block border border-[#D4A843] text-[#D4A843] hover:bg-[#D4A843] hover:text-black font-bold px-6 py-2 rounded-lg text-sm transition-colors" data-id="element-228">
          VIEW ALL CASES
        </a>
      </div>
    </div>;
}
function ContactoContent({
  isMobile = false
}: {
  isMobile?: boolean;
}) {
  const [formData, setFormData] = useState({
    nombre: '',
    negocio: '',
    telefono: '',
    inversion: '',
    mensaje: ''
  });
  const [sendStatus, setSendStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSendStatus('sending');
    try {
      const templateParams = {
        nombre: formData.nombre,
        email: '',
        negocio: formData.negocio || 'Not specified',
        telefono: formData.telefono,
        inversion: formData.inversion || 'Not specified',
        mensaje: formData.mensaje || 'No additional message',
        to_email: 'doctor.ecommerceus@gmail.com',
        page_url: window.location.href,
        date: new Date().toLocaleString()
      };
      // 1) Email para ADMIN
      await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ADMIN, templateParams, EMAILJS_PUBLIC_KEY);
      // 2) Email AUTO-REPLY (solo si hay email)
      if (templateParams.email) {
        await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_AUTOREPLY, templateParams, EMAILJS_PUBLIC_KEY);
      }
      setSendStatus('sent');
      setFormData({
        nombre: '',
        negocio: '',
        telefono: '',
        inversion: '',
        mensaje: ''
      });
      setTimeout(() => setSendStatus('idle'), 4000);
    } catch (error) {
      console.error('EmailJS error:', error);
      setSendStatus('error');
      setTimeout(() => setSendStatus('idle'), 4000);
    }
  };
  return <div data-id="element-229">
      <h2 className={`gold-text font-bold mb-8 text-center ${isMobile ? 'text-2xl' : 'text-3xl'}`} data-id="element-230">
        You are one step away from the cure for your business.
      </h2>

      <div className={`grid gap-10 ${isMobile ? 'grid-cols-1' : 'grid-cols-5'}`} data-id="element-231">
        <div className={isMobile ? '' : 'col-span-3'} data-id="element-232">
          <form onSubmit={handleSubmit} className="space-y-4" data-id="element-233">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-234">
              <div data-id="element-235">
                <input type="text" name="nombre" required value={formData.nombre} onChange={handleChange} placeholder="Full Name *" className="w-full px-4 py-2.5 bg-[#0A0A0A] border border-gray-700 rounded-lg text-white focus:border-[#D4A843] focus:outline-none text-sm" data-id="element-236" />
              </div>
              <div data-id="element-237">
                <input type="text" name="negocio" value={formData.negocio} onChange={handleChange} placeholder="Business Name" className="w-full px-4 py-2.5 bg-[#0A0A0A] border border-gray-700 rounded-lg text-white focus:border-[#D4A843] focus:outline-none text-sm" data-id="element-238" />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-239">
              <div data-id="element-240">
                <input type="tel" name="telefono" required value={formData.telefono} onChange={handleChange} placeholder="Phone (WhatsApp) *" className="w-full px-4 py-2.5 bg-[#0A0A0A] border border-gray-700 rounded-lg text-white focus:border-[#D4A843] focus:outline-none text-sm" data-id="element-241" />
              </div>
              <div data-id="element-242">
                <select name="inversion" value={formData.inversion} onChange={handleChange} className="w-full px-4 py-2.5 bg-[#0A0A0A] border border-gray-700 rounded-lg text-white focus:border-[#D4A843] focus:outline-none text-sm" data-id="element-243">
                  <option value="" data-id="element-244">
                    How much are you currently investing in ads?
                  </option>
                  <option value="0-500" data-id="element-245">$0 - $500</option>
                  <option value="500-2000" data-id="element-246">$500 - $2,000</option>
                  <option value="2000+" data-id="element-247">$2,000+</option>
                </select>
              </div>
            </div>

            <div data-id="element-248">
              <textarea name="mensaje" value={formData.mensaje} onChange={handleChange} rows={3} placeholder="Write your biggest problem here..." className="w-full px-4 py-2.5 bg-[#0A0A0A] border border-gray-700 rounded-lg text-white focus:border-[#D4A843] focus:outline-none text-sm resize-none" data-id="element-249" />
            </div>

            <button type="submit" disabled={sendStatus === 'sending'} className={`w-full font-bold py-3 rounded-lg text-sm transition-all duration-300 ${sendStatus === 'sent' ? 'bg-green-500 text-white' : sendStatus === 'error' ? 'bg-red-500 text-white' : sendStatus === 'sending' ? 'gold-gradient text-black opacity-70 cursor-wait' : 'gold-gradient text-black gold-glow hover:scale-[1.02]'}`} data-id="element-250">
              {sendStatus === 'sending' && 'SENDING...'}
              {sendStatus === 'sent' && '✓ MESSAGE SENT!'}
              {sendStatus === 'error' && '✕ ERROR — TRY AGAIN'}
              {sendStatus === 'idle' && 'SEND MESSAGE'}
            </button>

            <p className="text-center text-gray-500 text-xs mt-2 flex items-center justify-center gap-1.5" data-id="element-251">
              <ZapIcon className="w-3.5 h-3.5 text-[#D4A843]" data-id="element-252" />
              We respond in less than 2 hours
            </p>
          </form>
        </div>

        <div className={`flex flex-col justify-center space-y-6 ${isMobile ? 'border-t border-gray-800 pt-8 mt-4' : 'col-span-2 pl-8 border-l border-gray-800'}`} data-id="element-253">
          <div className="flex items-center gap-4" data-id="element-254">
            <div className="w-10 h-10 rounded-full bg-[#1A1A1A] flex items-center justify-center flex-shrink-0 border border-[#D4A843]/30" data-id="element-255">
              <MapPinIcon className="w-5 h-5 text-[#D4A843]" data-id="element-256" />
            </div>
            <span className="text-gray-300 text-sm" data-id="element-257">
              1001 WATER ST, TAMPA FL 33602
            </span>
          </div>

          <div className="flex items-center gap-4" data-id="element-258">
            <div className="w-10 h-10 rounded-full bg-[#1A1A1A] flex items-center justify-center flex-shrink-0 border border-[#D4A843]/30" data-id="element-259">
              <PhoneIcon className="w-5 h-5 text-[#D4A843]" data-id="element-260" />
            </div>
            <span className="text-gray-300 text-sm" data-id="element-261">402-872-0639</span>
          </div>

          <div className="flex items-center gap-4" data-id="element-262">
            <div className="w-10 h-10 rounded-full bg-[#1A1A1A] flex items-center justify-center flex-shrink-0 border border-[#D4A843]/30" data-id="element-263">
              <MailIcon className="w-5 h-5 text-[#D4A843]" data-id="element-264" />
            </div>
            <span className="text-gray-300 text-sm" data-id="element-265">
              doctor.ecommerceus@gmail.com
            </span>
          </div>
        </div>
      </div>
    </div>;
}